import {endpoint} from '~/api';
import {ISelectItem} from '~/components/Controls/Select';
import {ID} from '~/lib/model';
import {ISellersJsonDiff} from '~/modules/Clients/models/SellersJson';
import {IBidder} from '~/modules/Bidders/models/Bidder';
import {IAccountInfo} from '~/modules/Clients/models/AccountInfo';
import {IAccountManager} from '~/modules/Clients/models/AccountManager';
import {DataCubeAgency, IAgency, IAgencyShort} from '~/modules/Clients/models/Agency';
import {IBlocking} from '~/modules/Clients/models/Blocking';
import {Target} from '~/modules/Clients/models/ConfigsManipulation';
import {Configuration} from '~/modules/Clients/models/Configuration';
import {IAgencyBiddersFilter, ISellerBlockingsFilter, ISellerSourcesFilter, ISellerFinanceFilter} from '~/modules/Clients/models/filters';
import {GlobalConfig} from '~/modules/Clients/models/GlobalConfig';
import {IABCategory} from '~/modules/Clients/models/IABCategory';
import {IPaymentDetails} from '~/modules/Clients/models/PaymentDetails';
import {ISalesRepresentative} from '~/modules/Clients/models/SalesRepresentative';
import {
    ISensitiveCategoryDeclaration,
    ISensitiveCategoryDeclarationRequest,
    ISensitiveCategoryReportRow,
    SensitiveCategory,
    SensitiveCategoryDecision
} from '~/modules/Clients/models/SensitiveCategory';
import {DataCube, IntegrationStatus, ISellerShort, ISellerPreview, ISeller, Mediation} from '~/modules/Clients/models/Seller';
import {ChargeSchema, ISource, SourceCategory, TrafficSource} from '~/modules/Sources/models/Source';
import { IFinanceTransaction, IBalance} from '~/modules/Clients/models/Finance';
import type { IAgenciesFilter } from '~/modules/Clients/models/Agency';
import { toUTCWithLocalTime } from '~/lib/date';
import { IPaginator } from '~/lib/paginator';
import { ILine } from "./models/Line";

export const fetchAgencies = (filter?: IAgenciesFilter) => {
  const url = new URLSearchParams();
  const { id, title, active, dspConnectionType, dspConnectionTypeExclude, accountManagerId } = filter || {};

  if (id) url.append('id', id);
  if (title) url.append('title', title);
  if (typeof active === 'boolean') url.append('active', active.toString());
  if (Array.isArray(dspConnectionType) && dspConnectionType.length) {
    dspConnectionType.forEach(v => url.append('dspConnectionType', v));
    if (dspConnectionTypeExclude) url.append('dspConnectionTypeExclude', 'true');
  }
  if (accountManagerId != null) url.append('accountManagerId', String(accountManagerId));

  const qs = url.toString();
  return endpoint.get<any[]>(`/agency${qs ? `?${qs}` : ''}`).then(r =>
      r.data.map(a => ({...a, isActive: a.isActive ?? a.active})) as IAgencyShort[]
  );
};

export const fetchCategories = () => endpoint.get<IABCategory[]>('/iab-categories').then(r => r.data);

export const fetchDataCubesAgencyForId = (agencyId: ID) => endpoint.get<DataCubeAgency[]>(`/agency/${agencyId}/data-cubes`).then(r => r.data);
export const fetchAgency = async (agencyId: ID) => {
    const agency = await endpoint.get<IAgency>(`/agency/${agencyId}`).then(r => r.data)
    const dataCubes = await fetchDataCubesAgencyForId(agencyId);
    return {...agency, dataCubes};
};

export const createAgency = async (agency: IAgency) => endpoint.post<IAgency>('/agency', agency).then(r => r.data);

export const updateDataCubesAgencyForId = (agencyId: ID, dataCubesIds: ID[]) => endpoint.post(`/agency/${agencyId}/data-cubes`, dataCubesIds).then(r => r.data);
export const updateAgency = async (agency: IAgency) => { 
    const editedAgency = await endpoint.post<IAgency>(`/agency/${agency.id}`, {
        ...agency,
        id: undefined
    }).then(r => r.data);
    await updateDataCubesAgencyForId(agency.id, agency.dataCubes);
    return editedAgency;
}

export const deleteAgency = (agency: IAgency | IAgencyShort) => endpoint.delete(`/agency/${agency.id}`).then(r => r.data);

export const toggleAgency = (agency: IAgency | IAgencyShort, active: boolean) => endpoint.request({
    url: `/agency/${agency.id}/active`,
    method: active ? 'POST' : 'DELETE'
}).then(() => ({...agency, active}));

export const deactivateAllAgencies = () => endpoint.delete('/agency/all/deactivate');
export const activateAllAgencies = () => endpoint.post('/agency/all/activate');

export const bulkAssignAgencyPreset = (agencyIds: ID[], presetId: ID) => endpoint.post('/agency/preset', {
    ids: agencyIds,
    update: presetId
});

export const bulkAssignSellerPreset = (sellerIds: ID[], presetId: ID) => endpoint.post('/sellers/preset', {
    ids: sellerIds,
    update: presetId
});


export interface ISellersPaginator {
    pageNo?: number;
    pageSize?: number;
    sortBy?: 'asc' | 'desc';
}

export interface ISellersFilter {
    id?: string;
    active?: boolean;
    name?: string;
    sellerType?: 'self_serve' | 'internal';
    accountManagerId?: number;
}

const setUrlParam = <T extends any>(params: URLSearchParams, obj: T, field: keyof T) => {
    if (obj[field] !== undefined && obj[field] !== null) {
        params.set(field.toString(), obj[field].toString());
    }
};

export const fetchSellers = (filter: ISellersFilter, paginator: ISellersPaginator) => {
    const params = new URLSearchParams();
    setUrlParam(params, paginator, 'pageNo');
    setUrlParam(params, paginator, 'pageSize');
    setUrlParam(params, paginator, 'sortBy');
    setUrlParam(params, filter, 'active');
    setUrlParam(params, filter, 'name');
    setUrlParam(params, filter, 'sellerType');
    setUrlParam(params, filter, 'id');
    setUrlParam(params, filter, 'accountManagerId');
    return endpoint
        .get<IPaginator<ISellerShort>>(`/sellers-view?${params.toString()}`)
        .then(r => r.data);
};

export const fetchAllSellers = () => {
    const params = new URLSearchParams();

    return endpoint
        .get<IPaginator<ISellerShort>>(`/sellers-view?${params.toString()}`)
        .then((r) => r.data.items);
};

export const fetchSellerPreviews = () =>
    endpoint.get<ISellerPreview[]>('/sellers/previews').then(r => r.data);

export const fetchSalesRepresentatives = () => endpoint.get<ISalesRepresentative[]>('/sales-representatives').then(r => r.data);
export const createSalesRepresentative = (salesRepresentative: Pick<ISalesRepresentative, 'name'>) =>
    endpoint.post<ISalesRepresentative>('/sales-representatives', salesRepresentative).then(r => r.data);
export const deleteSalesRepresentative = (salesRepresentativeId: ID) =>
    endpoint.delete(`/sales-representatives/${salesRepresentativeId}`).then(() => undefined);
export const updateSalesRepresentative = (id: ID, data: Pick<ISalesRepresentative, 'name'>) =>
    endpoint.put<ISalesRepresentative>(`/sales-representatives/${id}`, data).then(r => r.data);
export const fetchAccountManagers = () => endpoint.get<IAccountManager[]>('/account-managers').then(r => r.data);
export const createAccountManager = (accountManager: Pick<IAccountManager, 'name'>) =>
    endpoint.post<IAccountManager>('/account-managers', { ...accountManager, active: true }).then(r => r.data);
export const deleteAccountManager = (accountManagerId: ID) =>
    endpoint.delete(`/account-managers/${accountManagerId}`).then(() => undefined);
export const updateAccountManager = (id: ID, data: Pick<IAccountManager, 'name'>) =>
    endpoint.put<IAccountManager>(`/account-managers/${id}`, data).then(r => r.data);
export const fetchAccountManagersForAgency = () => endpoint.get<IAccountManager[]>('/agency/managers/active').then(r => r.data);
export const createAgencyAccountManager = (accountManager: { name: string; slackUserId: string }) =>
    endpoint.post<IAccountManager>('/agency-managers', { ...accountManager, active: true }).then(r => r.data);
export const deleteAgencyAccountManager = (accountManagerId: ID) =>
    endpoint.delete(`/agency-managers/${accountManagerId}`).then(() => undefined);
export const updateAgencyAccountManager = (id: ID, data: { name: string; slackUserId?: string }) =>
    endpoint.put<IAccountManager>(`/agency-managers/${id}`, data).then(r => r.data);

const prepareSellerSubmitData = (record) => ({
    ...record,
    expectedUsDau: record.expectedUsDau === '' ? null : record.expectedUsDau,
    expectedUsSpend: record.expectedUsSpend === '' ? null : record.expectedUsSpend,
    companyEmail: record.companyEmail === '' ? null : record.companyEmail,
});
export const fetchSeller = (sellerId: ID) => endpoint.get<ISeller>(`/sellers/${sellerId}`).then(r => r.data);

export const createSeller = (seller: ISeller) => endpoint.post<ISeller>('/sellers', prepareSellerSubmitData(seller)).then(r => r.data);

export const updateSeller = (seller: ISeller) => endpoint.put<ISeller>(`/sellers/${seller.id}`, prepareSellerSubmitData(seller)).then(r => r.data);

export const deleteSeller = (seller: ISeller) => endpoint.delete(`/sellers/${seller.id}`).then(r => r.data);

export const toggleSeller = (seller: ISeller, active: boolean) => endpoint.request({
    url: `/sellers/${seller.id}/active`,
    method: active ? 'POST' : 'DELETE'
}).then(() => ({...seller, active}));

export const fetchAgencyBidders = (filter: IAgencyBiddersFilter) => {
    const params = new URLSearchParams();
    const {active} = filter || {};
    if (typeof active === 'boolean') {
        params.append('enabled', active.toString());
    }
    params.append('configIds', 'true');
    const suffix = params.toString();
    return endpoint.get<any>(`/agency/${filter.agencyId}/bidder${suffix ? `?${suffix}` : ''}`).then(r => {
        const data = r.data;
        if (Array.isArray(data)) {
            return data as IBidder[];
        }
        if (Array.isArray(data?.items)) {
            return data.items as IBidder[];
        }
        return [];
    });
};

export const fetchSellerSources = (filter: ISellerSourcesFilter) =>
    endpoint.get<ISource[]>(`/sellers/${filter.sellerId}/sources`).then(r => r.data);
export const fetchSellerAccountInfo = (sellerId: ID) =>
    endpoint.get<IAccountInfo>(`/account-info/${sellerId}`).then(r => r.data);
export const forceReleasePayments = (sellerId: ID) => endpoint.post(`/payment/account/force-release/${sellerId}`).then(r => r.data)
export const forcePayments = (sellerId: ID) => endpoint.post(`/payment/force/${sellerId}`).then(r => r.data) 

export const fetchPaymentDetailsEndpoint = (sellerId: ID) => endpoint.get<IPaymentDetails>(`/sellers/${sellerId}/paymentDetails`);

export const fetchPaymentDetails = (sellerId: ID) => fetchPaymentDetailsEndpoint(sellerId).then(r => ({
    ...r.data,
    sellerId: sellerId
})).catch(() => null);

export const updatePaymentDetails = (paymentDetails: IPaymentDetails) => {
    const data = new FormData();
    const {sellerId, ...otherProps} = paymentDetails;
    Object.keys(otherProps).forEach(key => data.append(key, paymentDetails[key]));
    return endpoint.post(`/sellers/${sellerId}/paymentDetails`, data).then<IPaymentDetails>(() => paymentDetails);
};

const getConfigsURL = (target: Target) => `/${target === Target.SELLERS ? 'sellers' : 'sources'}/configuration`;

const parseConfigsUpdate = (configuration: Configuration): Record<string, string> => {
    const value = (configuration ?? '').trim();
    if (!value) {
        throw new Error('Configuration is empty');
    }
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Configuration must be a JSON object');
    }
    const result: Record<string, string> = {};
    for (const [key, entryValue] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof entryValue !== 'string') {
            throw new Error(`Configuration value for "${key}" must be a string`);
        }
        result[key] = entryValue;
    }
    return result;
};

export const createConfigs = (target: Target, ids: number[], config: Configuration) => endpoint.request({
    url: getConfigsURL(target),
    method: 'POST',
    data: {
        ids,
        update: parseConfigsUpdate(config)
    }
});
export const updateConfigs = (target: Target, ids: number[], config: Configuration) => endpoint.request({
    url: getConfigsURL(target),
    method: 'PUT',
    data: {
        ids,
        update: parseConfigsUpdate(config)
    }
});
export const deleteConfigs = (target: Target, ids: number[], keys: string[]) => endpoint.request({
    url: getConfigsURL(target),
    method: 'DELETE',
    data: {
        ids,
        update: keys
    }
});

export const getGlobalSellersConfig = () => endpoint.get<GlobalConfig>('/exchange-config').then(r => r.data);
export const createGlobalSellersConfig = (config: GlobalConfig) => endpoint.post('/exchange-config', config).then(r => r.data);

export const getGlobalTagIdConfig = () => endpoint.get<any>('/tagid-config')
    .then(r => ({ config: JSON.stringify(r.data || {}) }))
    .catch(() => ({ config: '{}' }));
export const createGlobalTagIdConfig = (config: any) => endpoint.post('/tagid-config', config).then(r => r.data);

export const fetchChargeSchemas = () => endpoint.get<ISelectItem<ChargeSchema>[]>('/sources/chargeSchema').then(r => r.data);
export const fetchTrafficSource = () => endpoint.get<ISelectItem<TrafficSource>[]>('/sources/trafficSource').then(r => r.data);
export const fetchSourceCategories = () => endpoint.get<ISelectItem<SourceCategory>[]>('/sources/category').then(r => r.data);
export const fetchIntegrationStatuses = () => endpoint.get<ISelectItem<IntegrationStatus>[]>('/sellers/integration-status').then(r => r.data);
export const fetchDataCubes = () => endpoint.get<ISelectItem<DataCube>[]>('/sellers/data-cubes').then(r => r.data);
export const fetchMediation = () => endpoint.get<ISelectItem<Mediation>[]>('/sellers/mediation').then(r => r.data);
export const fetchDataCubesAgency = () => endpoint.get<DataCubeAgency[]>('/agency/data-cubes/all').then(r => r.data);
    
export const fetchSellerBlockings = (filter: ISellerBlockingsFilter) =>
    endpoint.get<IBlocking[]>(`/sellers/${filter.sellerId}/blockingSettings`).then(r => r.data);
export const deleteSellerBlockings = (filter: ISellerBlockingsFilter) =>
    endpoint.delete(`/sellers/${filter.sellerId}/blockingSettings`).then(r => r.data);
export const deleteBlocking = (blocking: IBlocking) =>
    endpoint.delete(`/sellers/${blocking.sellerId}/blockingSettings/${blocking.id}`).then(r => r.data);
export const createBlocking = (blocking: IBlocking) =>
    endpoint.post(`/sellers/${blocking.sellerId}/blockingSettings`, blocking).then(r => r.data);
export const updateBlocking = (blocking: IBlocking) =>
    endpoint.put(`/sellers/${blocking.sellerId}/blockingSettings/${blocking.id}`, blocking).then(r => r.data);

export const fetchSellerFinanceTransactions = (filter: ISellerFinanceFilter) =>
    endpoint.get<IFinanceTransaction>(`transactions/finance/${filter.sellerId}`).then(r => r.data);

export const prepareBalanceSubmitData = (sellerId: number, balance: IBalance) => {
    return {
        "accountId": sellerId,
        "amount": balance.actionType === 'add' ? balance.changeBalance : -balance.changeBalance,
        "revenuePeriod": {
           "start": toUTCWithLocalTime(balance.revenuePeriod.startDate)?.toISOString(),
           "end": toUTCWithLocalTime(balance.revenuePeriod.endDate)?.toISOString()
        },
       "comment": balance.comment
     }
}

export const updateFinanceTransaction = (sellerId: number, balance: IBalance) => {
    return endpoint.post(`/transactions`, prepareBalanceSubmitData(sellerId, balance)).then(r => r.data);
}

export interface ILinesPaginator {
    pageNo?: number;
    pageSize?: number;
}

export interface ILinesFilter {
    id?: string;
    status?: string;
    excludeDeleted?: boolean;
    scope?: string;
    externalSellerId?: string;
    sellerType?: string;
    domain?: string;
    resellerDomain?: string;
}

export const fetchLines = (agencyId: number, filter?: ILinesFilter, paginator?: ILinesPaginator): Promise<IPaginator<ILine>> => {
  const params = new URLSearchParams();

  if (paginator) {
    setUrlParam(params, paginator, 'pageNo');
    setUrlParam(params, paginator, 'pageSize');
  }

  if (filter) {
    setUrlParam(params, filter, 'id');
    setUrlParam(params, filter, 'status');
    setUrlParam(params, filter, 'excludeDeleted');
    setUrlParam(params, filter, 'scope');
    setUrlParam(params, filter, 'externalSellerId');
    setUrlParam(params, filter, 'sellerType');
    setUrlParam(params, filter, 'domain');
    setUrlParam(params, filter, 'resellerDomain');
  }

  const queryString = params.toString();
  return endpoint.get<IPaginator<ILine>>(`/agency/${agencyId}/distribution/lines${queryString ? '?' + queryString : ''}`).then(r => r.data);
};

export const fetchLine = (agencyId: number, lineId: number): Promise<ILine> =>
  endpoint.get<ILine>(`/agency/${agencyId}/distribution/lines/${lineId}`).then(r => r.data);

export const createLine = (line: ILine): Promise<ILine> => {
  if (!line.agencyId) throw new Error('agencyId is required');
  return endpoint.post<ILine>(`/agency/${line.agencyId}/distribution/lines`, line).then(r => r.data);
};

export const updateLine = (line: ILine): Promise<ILine> => {
  if (!line.agencyId) throw new Error('agencyId is required');
  return endpoint.put<ILine>(`/agency/${line.agencyId}/distribution/lines/${line.id}`, {
    ...line,
    id: undefined,
    agencyId: undefined
  }).then(r => r.data);
};

export const updateLineStatus = (
  line: { agencyId: number; id: number },
  status: string
): Promise<{ success: boolean; status: string }> =>
  endpoint
    .put<{ success: boolean; status: string }>(
      `/agency/${line.agencyId}/distribution/lines/${line.id}/status`,
      { status }
    )
    .then(r => r.data);

export const deleteLine = (line: ILine) => {
  if (!line.agencyId) throw new Error('agencyId is required');
  return endpoint.delete(`/agency/${line.agencyId}/distribution/lines/${line.id}`).then(r => r.data);
};

export async function fetchAgencyDomains(agencyId: number, domainPrefix?: string) {
  const response = await endpoint.get(`/agency/${agencyId}/ad-system-domains`, {
    params: domainPrefix ? { domainPrefix } : {},
  });
  return response.data;
}

export const fetchSellersJsonDiff = () =>
    endpoint.get<ISellersJsonDiff>('/sellers-json/diff').then(r => r.data);

export const fetchSellerSensitiveCategories = (sellerId: ID) =>
    endpoint.get<ISensitiveCategoryDeclaration[]>(`/sellers/${sellerId}/sensitiveCategories`).then(r => r.data);
export const saveSellerSensitiveCategory = (
    sellerId: ID,
    category: SensitiveCategory,
    request: ISensitiveCategoryDeclarationRequest
) =>
    endpoint.put<ISensitiveCategoryDeclaration>(`/sellers/${sellerId}/sensitiveCategories/${category}`, request)
        .then(r => r.data);
export const deleteSellerSensitiveCategory = (sellerId: ID, category: SensitiveCategory) =>
    endpoint.delete(`/sellers/${sellerId}/sensitiveCategories/${category}`).then(r => r.data);
export const fetchSensitiveCategoryReport = (category: SensitiveCategory, decision?: SensitiveCategoryDecision) =>
    endpoint.get<ISensitiveCategoryReportRow[]>('/sensitiveCategories/report', {params: {category, decision}})
        .then(r => r.data);
